import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import pluralize from "pluralize";
import { Button, Skeleton, Textarea } from "@/components/ui";
import { ReviewRating } from "@/components/reviews";
import { SocialNetworkReview, SocialNetworkReviewReply } from "@/services/bedrock";
import { getErrorMessage, printDate, useCreateSocialProfileReviewReply, useSocialProfileReviewReplies } from "@/utils";

function ReviewReplyItem({ reply }: { reply: SocialNetworkReviewReply }) {
  const authorName = reply.displayName || "Unknown";
  const nestedReplies = reply.reviewReplies;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1 rounded-md bg-slate-50 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-800">{authorName}</span>
          {reply.publishedAt && <span className="text-xs text-slate-500 shrink-0">{printDate(reply.publishedAt)}</span>}
        </div>
        {reply.content && (
          <div
            className="text-sm text-slate-700 whitespace-pre-wrap [&_span]:font-semibold"
            dangerouslySetInnerHTML={{ __html: reply.content }}
          />
        )}
      </div>
      {nestedReplies && nestedReplies.length > 0 && (
        <div className="ml-4 flex flex-col gap-2 border-l border-slate-200 pl-3">
          {nestedReplies.map(nestedReply => (
            <ReviewReplyItem key={nestedReply.id} reply={nestedReply} />
          ))}
        </div>
      )}
    </div>
  );
}

export function SocialProfileReviewItem({
  review,
  userId,
  socialIdentityId,
}: {
  review: SocialNetworkReview;
  userId: string | number;
  socialIdentityId: string;
}) {
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  let replyLabel: string | null = null;
  if (!!review.replyCount) {
    replyLabel = `View ${pluralize("reply", review.replyCount, true)}`;
  } else if (review.sourceSystem === "FB") {
    replyLabel = `Reply (${review.replyCount ?? 0})`;
  }
  const reviewerName = review.displayName || "Anonymous";

  const {
    data: replies,
    isLoading: repliesLoading,
    isError: repliesError,
    error: repliesFetchError,
  } = useSocialProfileReviewReplies(userId, socialIdentityId, review.id, isReplying);

  const replyMutation = useCreateSocialProfileReviewReply(userId, socialIdentityId, review.id, {
    onSuccess: () => setReplyText(""),
  });

  const handleCancelReply = () => {
    setIsReplying(false);
    setReplyText("");
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{reviewerName}</span>
          {review.publishedAt && <span className="text-sm text-slate-500">{printDate(review.publishedAt)}</span>}
        </div>
        <ReviewRating className="shrink-0" review={review} />
      </div>
      {review.content && <div className="text-sm text-slate-700 whitespace-pre-wrap">{review.content}</div>}

      {!isReplying && replyLabel && (
        <Button
          variant="ghost"
          size="xs"
          className="self-start rounded-full px-2 text-xs font-semibold text-primary hover:bg-primary/10 hover:text-primary"
          onClick={() => setIsReplying(true)}
        >
          <MessageCircle className="size-3" />
          {replyLabel}
        </Button>
      )}

      {isReplying && (
        <div className="flex flex-col gap-3 pt-1 border-t border-slate-100">
          {repliesLoading && (
            <div className="flex flex-col gap-3">
              {Array.from({ length: Math.min(review.replyCount ?? 2, 3) }, (_, key) => (
                <div key={key} className="flex flex-col gap-1.5 rounded-md bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-3.5 w-full" />
                  <Skeleton className="h-3.5 w-4/5" />
                </div>
              ))}
            </div>
          )}
          {repliesError && (
            <p className="text-sm text-destructive">
              {getErrorMessage(repliesFetchError) || "Failed to load replies."}
            </p>
          )}
          {!repliesLoading && !repliesError && replies && replies.length > 0 && (
            <div className="flex flex-col gap-2">
              {replies.map(reply => (
                <ReviewReplyItem key={reply.id} reply={reply} />
              ))}
            </div>
          )}

          {review.sourceSystem === "FB" && (
            <>
              <Textarea
                placeholder="Write a reply..."
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                rows={3}
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={handleCancelReply}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!replyText.trim() || replyMutation.isPending}
                  loading={replyMutation.isPending}
                  onClick={() => replyMutation.mutate(replyText.trim())}
                >
                  Reply
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
